import React, { useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { Phone, PhoneOff, Video, User } from 'lucide-react';
import { useAppNavigate } from '../context/NavigationContext';

const SOCKET_URL = API_BASE_URL.replace(/\/api$/, '');

/**
 * IncomingCallOverlay — global overlay that listens for incoming-call
 * socket events and shows a ringing UI. Renders at app root level.
 */
const IncomingCallOverlay = () => {
  const { user } = useAuth();
  const navigate = useAppNavigate();
  const [call, setCall] = useState(null); // { meetingId, meetingCode, callType, callerName, callerAvatar, roomId, title }
  const socketRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-user-room', { userId: user._id || user.id });
    });

    socket.on('incoming-call', (data) => {
      setCall(data);
      // Auto-dismiss after 30 seconds
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCall(null), 30000);
    });

    return () => {
      socket.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [user]);

  const accept = () => {
    if (!call) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const code = call.meetingCode;
    setCall(null);
    if (code) {
      navigate(`/meeting/${code}`);
    } else if (call.roomId) {
      navigate(`/projects/${call.roomId}`);
    }
  };

  const decline = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (socketRef.current && call) {
      socketRef.current.emit('call-declined', {
        meetingId: call.meetingId,
        userId: user?._id || user?.id,
        reason: 'declined'
      });
    }
    setCall(null);
  };

  if (!call) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface rounded-3xl border border-surface-border p-8 max-w-sm w-full mx-4 text-center shadow-overlay">
        {/* Pulse ring */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
          <div className="absolute inset-2 rounded-full bg-primary/30 animate-ping" style={{ animationDelay: '0.3s' }} />
          <div className="relative w-24 h-24 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
            {call.callerAvatar ? (
              <img src={call.callerAvatar} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <User className="w-10 h-10 text-primary" />
            )}
          </div>
        </div>

        <h2 className="text-xl font-bold text-white mb-1">{call.callerName || 'Someone'}</h2>
        <p className="text-dark-400 text-sm mb-1">
          Incoming {call.callType === 'audio' ? 'audio' : 'video'} call
        </p>
        {call.title && <p className="text-dark-500 text-xs mb-6">{call.title}</p>}

        <div className="flex items-center justify-center gap-6">
          <button
            onClick={decline}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-lg"
          >
            <PhoneOff className="w-7 h-7" />
          </button>
          <button
            onClick={accept}
            className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors shadow-lg animate-bounce"
          >
            {call.callType === 'audio' ? <Phone className="w-7 h-7" /> : <Video className="w-7 h-7" />}
          </button>
        </div>

        <div className="flex justify-center gap-1 mt-4">
          <span className="text-dark-500 text-xs">Ringing…</span>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallOverlay;
